import React, { useRef } from "react";
import { useLoading } from "../context/LoadingContext";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(useGSAP);

export default function Loader() {
  const { isLoading, message, isFullScreen } = useLoading();
  const logoRef = useRef(null);

  const loaderSize = "clamp(8.75rem, 32vw, 11.25rem)";
  const logoSize = "clamp(7rem, 26vw, 9.375rem)";

  useGSAP(() => {
    if (!logoRef.current || !isLoading) return;

    // Configuration initiale 3D pour GSAP
    gsap.set(logoRef.current, { transformPerspective: 1200, transformOrigin: "center center" });

    const tl = gsap.timeline({ repeat: -1 });

    // Séquence organique et "erratique" avec des fluides (élastiques, back, power)
    tl.to(logoRef.current, {
      rotationY: 180,
      rotationX: 180,
      scale: 0.7,
      y: -20,
      duration: 0.7,
      ease: "power2.inOut"
    })
    .to(logoRef.current, {
      rotationY: 360,
      rotationX: 360,
      scale: 1.3,
      y: 15,
      duration: 0.9,
      ease: "back.out(2)" // Effet de rebond naturel
    })
    .to(logoRef.current, {
      rotationY: 540,
      rotationZ: -15,
      scale: 0.6,
      y: -40,
      duration: 0.6,
      ease: "power1.inOut"
    })
    .to(logoRef.current, {
      rotationY: 720,
      rotationX: 720,
      rotationZ: 0,
      scale: 1,
      y: 0,
      duration: 1.4,
      ease: "elastic.out(1, 0.4)" // Amortissement très naturel à la fin de la boucle
    });
  }, { dependencies: [isLoading] });

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          key="smart-loader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.3 } }}
          className="bg-workspace flex flex-col items-center justify-center"
          style={{
            position: isFullScreen ? "fixed" : "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: isFullScreen ? 9999 : 50,
            overflow: "hidden",
            padding: "clamp(1rem, 4vw, 2rem)",
          }}
        >
          <div
            className="relative flex items-center justify-center mb-6"
            style={{ width: loaderSize, height: loaderSize }}
          >
            {/* Logo avec animation physique GSAP */}
            <img
              ref={logoRef}
              src="/assets/loader/loader.svg"
              alt="Logo Hipparchus"
              className="absolute z-10 object-contain drop-shadow-2xl"
              style={{ width: logoSize, height: logoSize }}
            />
          </div>

          {/* Texte dynamique */}
          <div className="h-8 flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={message}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <p className="text-primary font-medium text-center text-lg opacity-80 tracking-wide m-0">
                  {message}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
